import { IVFSResponse, Tag } from '../types';
import BaseAPI from '../BaseAPI';
import {
  FileType,
  FolderType,
  SharePermission,
  FolderPermission,
  Speaker,
  CompactSegment,
  SegmentEditRecord,
  TimeStampedLine,
  SortBy,
  SortOrderBy,
  FetchFileSystemResponseBody,
  GetFileReturnType,
  AskAIResponse,
  MediaAnalysisOptions,
  TranscriptSearchResult,
  SendInvitationParams,
  SendInvitationResponse,
  AcceptInvitationResponse,
  GetSharedWithMeResponse,
} from './types';

/**
 * File System API
 * Handles all file and folder related operations
 */
class FilesAPI extends BaseAPI {
  /**
   * Fetch files with optional filters
   */
  async getFiles(
    params: {
      teamId?: string;
      folderId?: string;
      ownerId?: string;
      sortBy?: SortBy;
      sortOrder?: SortOrderBy;
      page?: number;
      limit?: number;
      searchQuery?: string;
    } = {},
  ): Promise<IVFSResponse<FetchFileSystemResponseBody>> {
    const queryParams = new URLSearchParams();

    // Add all params to query string
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, String(value));
      }
    });

    const queryString = queryParams.toString();
    const url = `/v2/files${queryString ? `?${queryString}` : ''}`;

    return this.wrapResponse({
      promise: this.apiInstance.get(url),
      errorMessage: 'Failed to fetch files',
    });
  }

  /**
   * Get file details by ID
   */
  async getFile(fileId: string): Promise<IVFSResponse<GetFileReturnType>> {
    return this.wrapResponse({
      promise: this.apiInstance.get<IVFSResponse<GetFileReturnType>>(
        `/v2/files/${fileId}`,
      ),
      errorMessage: `Failed to get file: ${fileId}`,
    });
  }

  /**
   * Get a shared file by share ID
   * Used by the public share view page
   */
  async getSharedFile(
    shareId: string,
  ): Promise<IVFSResponse<GetFileReturnType>> {
    return this.wrapResponse({
      promise: this.apiInstance.get<IVFSResponse<GetFileReturnType>>(
        `/v2/files/shared/${shareId}`,
      ),
      errorMessage: `Failed to get shared file: ${shareId}`,
    });
  }

  /**
   * Create a new folder
   */
  async createFolder(params: {
    teamId: string;
    name: string;
  }): Promise<IVFSResponse<{ folder: FolderType }>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<IVFSResponse<{ folder: FolderType }>>(
        `/v2/folders/${params.teamId}`,
        {
          name: params.name,
        },
      ),
      errorMessage: 'Failed to create folder',
    });
  }

  /**
   * Share a file with others
   */
  async shareFile(params: {
    fileId: string;
    permissions?: SharePermission[];
  }): Promise<IVFSResponse<{ shareId: string }>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<IVFSResponse<{ shareId: string }>>(
        `/v2/files/actions/${params.fileId}/share`,
        {
          permissions: params.permissions,
        },
      ),
      errorMessage: `Failed to share file: ${params.fileId}`,
    });
  }

  /**
   * Remove sharing for a file
   */
  async unshareFile(fileId: string): Promise<IVFSResponse<{}>> {
    return this.wrapResponse({
      promise: this.apiInstance.delete<IVFSResponse<{}>>(
        `/v2/files/actions/${fileId}/share`,
      ),
      errorMessage: `Failed to unshare file: ${fileId}`,
    });
  }

  /**
   * Update file details
   */
  async updateFile(
    id: string,
    changes: {
      name?: string;
      description?: string;
    },
  ): Promise<IVFSResponse<{ file: FileType }>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<IVFSResponse<{ file: FileType }>>(
        `/v2/files/${id}`,
        changes,
      ),
      errorMessage: `Failed to update file: ${id}`,
    });
  }

  /**
   * Delete a file
   */
  async deleteFile(fileId: string): Promise<IVFSResponse<void>> {
    return this.wrapResponse({
      promise: this.apiInstance.delete<IVFSResponse<void>>(
        `/v2/files/actions/${fileId}/delete`,
      ),
      errorMessage: `Failed to delete file: ${fileId}`,
    });
  }

  /**
   * Move a file
   */
  async moveFile({
    fileId,
    toTeamId,
    toFolderId,
  }: {
    fileId: string;
    toTeamId: string;
    toFolderId: string;
  }): Promise<IVFSResponse<any>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<IVFSResponse<{ file: FileType }>>(
        `/v2/files/actions/${fileId}/move`,
        {
          toTeamId,
          toFolderId,
        },
      ),
      errorMessage: `Failed to move file: ${fileId}`,
    });
  }

  /**
   * Get download URL for a file
   */
  async getDownloadUrl({
    fileId,
    profile,
  }: {
    fileId: string;
    profile: string;
  }): Promise<IVFSResponse<{ url: string }>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<IVFSResponse<{ url: string }>>(
        `/v2/files/actions/${fileId}/download/${profile}`,
      ),
      errorMessage: `Failed to get download URL for file: ${fileId}`,
    });
  }

  /**
   * Ask AI a question about a file
   */
  async askAI({
    fileId,
    question,
  }: {
    fileId: string;
    question: string;
  }): Promise<IVFSResponse<AskAIResponse>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<IVFSResponse<AskAIResponse>>(
        `/v2/files/${fileId}/ask`,
        {
          question,
        },
      ),
      errorMessage: `Failed to ask AI about file: ${fileId}`,
    });
  }

  /**
   * Ask AI a multimodal question about a file
   */
  async askAIMultimodal({
    fileId,
    promptText,
    mediaAnalysisOptions,
  }: {
    fileId: string;
    promptText: string;
    mediaAnalysisOptions?: MediaAnalysisOptions;
  }): Promise<IVFSResponse<AskAIResponse>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<IVFSResponse<AskAIResponse>>(
        `/v2/files/${fileId}/ask/multimodal`,
        {
          promptText,
          mediaAnalysisOptions,
        },
      ),
      errorMessage: `Failed to ask AI multimodal about file: ${fileId}`,
    });
  }

  /**
   * Delete an AI chat session
   */
  async deleteAskAISession(
    fileId: string,
    sessionId: string,
  ): Promise<IVFSResponse<any>> {
    return this.wrapResponse({
      promise: this.apiInstance.delete<IVFSResponse<any>>(
        `/v2/files/${fileId}/ask/${sessionId}`,
      ),
      errorMessage: `Failed to delete AI chat session: ${sessionId}`,
    });
  }

  /**
   * Delete a template output
   */
  async deleteTemplateOutput(
    fileId: string,
    templateId: string,
  ): Promise<IVFSResponse<any>> {
    return this.wrapResponse({
      promise: this.apiInstance.delete<IVFSResponse<void>>(
        `/v2/files/actions/${fileId}/template-outputs/${templateId}`,
      ),
      errorMessage: `Failed to delete template output: ${templateId}`,
    });
  }

  /**
   * Save enhanced notes
   */
  async saveEnhancedNotes({
    fileId,
    responseText,
  }: {
    fileId: string;
    responseText: string;
  }): Promise<IVFSResponse<void>> {
    return this.wrapResponse({
      promise: this.apiInstance.put<IVFSResponse<void>>(
        `/v2/files/actions/${fileId}/template-outputs/system/ENHANCED_NOTES`,
        {
          responseText,
        },
      ),
      errorMessage: `Failed to save enhanced notes for file: ${fileId}`,
    });
  }

  /**
   * Update a transcript segment
   */
  async updateTranscriptSegment({
    fileId,
    segmentId,
    newText,
    teamId,
  }: {
    fileId: string;
    segmentId: string | number;
    newText: string;
    teamId?: string;
  }): Promise<
    IVFSResponse<{
      editRecord: SegmentEditRecord;
      compactSegment: CompactSegment;
    }>
  > {
    return this.wrapResponse({
      promise: this.apiInstance.post<
        IVFSResponse<{
          editRecord: SegmentEditRecord;
          compactSegment: CompactSegment;
        }>
      >(`/v2/files/${fileId}/transcript/compact-segments/${segmentId}/update`, {
        text: newText,
        teamId,
      }),
      errorMessage: `Failed to update transcript segment: ${segmentId}`,
    });
  }

  /**
   * Restore a transcript segment to a previous version
   */
  async restoreTranscriptSegment({
    fileId,
    segmentId,
    versionId,
    teamId,
  }: {
    fileId: string;
    segmentId: string | number;
    versionId: string;
    teamId?: string;
  }): Promise<
    IVFSResponse<{
      editRecord: SegmentEditRecord;
      compactSegment: CompactSegment;
    }>
  > {
    return this.wrapResponse({
      promise: this.apiInstance.post<
        IVFSResponse<{
          editRecord: SegmentEditRecord;
          compactSegment: CompactSegment;
        }>
      >(
        `/v2/files/${fileId}/transcript/compact-segments/${segmentId}/restore`,
        {
          versionId,
          teamId,
        },
      ),
      errorMessage: `Failed to restore transcript segment: ${segmentId}`,
    });
  }

  /**
   * Save notes
   */
  async saveNotes({
    fileId,
    notes,
  }: {
    fileId: string;
    notes: TimeStampedLine[];
  }): Promise<IVFSResponse<void>> {
    return this.wrapResponse({
      promise: this.apiInstance.put<IVFSResponse<void>>(
        `/v2/files/${fileId}/notes`,
        { notes },
      ),
      errorMessage: `Failed to save notes for file: ${fileId}`,
    });
  }

  /**
   * Trim a file
   */
  async trim({
    fileId,
    startMS,
    endMS,
  }: {
    fileId: string;
    startMS: number;
    endMS: number;
  }): Promise<IVFSResponse<{ file: FileType }>> {
    return this.wrapResponse({
      promise: this.apiInstance.delete<IVFSResponse<{ file: FileType }>>(
        `/v2/files/actions/${fileId}/trim`,
        {
          params: { startMS, endMS },
        },
      ),
      errorMessage: `Failed to trim file: ${fileId}`,
    });
  }

  /**
   * Get upload URLs for files
   */
  async getUploadUrls({
    teamId,
    folderId,
    files,
  }: {
    teamId: string;
    folderId: string;
    files: { name: string; contentType: string }[];
  }): Promise<
    IVFSResponse<{ uploadParams: { fileId: string; uploadUrl: string }[] }>
  > {
    return this.wrapResponse({
      promise: this.apiInstance.post<
        IVFSResponse<{ uploadParams: { fileId: string; uploadUrl: string }[] }>
      >(`/v2/files/upload/urls/${teamId}/${folderId}/`, {
        files,
      }),
      errorMessage: 'Failed to get upload URLs',
    });
  }

  /**
   * Import a file from a URL
   */
  async importFile({
    teamId,
    folderId,
    url,
    options,
  }: {
    teamId: string;
    folderId: string;
    url: string;
    options?: any;
  }): Promise<IVFSResponse<{ file: FileType }>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<IVFSResponse<{ file: FileType }>>(
        `/v2/files/import/${teamId}/${folderId}`,
        {
          url,
          options,
        },
      ),
      errorMessage: 'Failed to import file',
    });
  }

  /**
   * Finalize uploads
   */
  async finalizeUploads({
    teamId,
    folderId,
    fileId,
    contentType,
    name,
    description,
  }: {
    teamId: string;
    folderId: string;
    fileId: string;
    contentType: string;
    name: string;
    description?: string;
  }): Promise<IVFSResponse<{ file: FileType }>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<IVFSResponse<{ file: FileType }>>(
        `/v2/files/upload/finalize/${teamId}/${folderId}`,
        {
          file: { fileId, contentType, name, description },
        },
      ),
      errorMessage: `Failed to finalize upload for file: ${fileId}`,
    });
  }

  /**
   * Reprocess a file
   */
  async reprocessFile(
    fileId: string,
  ): Promise<IVFSResponse<{ file: FileType }>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<IVFSResponse<{ file: FileType }>>(
        `/v2/files/actions/${fileId}/reprocess`,
      ),
      errorMessage: `Failed to reprocess file: ${fileId}`,
    });
  }

  /**
   * Save template output
   */
  async saveTemplateOutput({
    fileId,
    templateId,
    responseText,
    promptTitle,
  }: {
    fileId: string;
    templateId: string;
    responseText: string;
    promptTitle?: string;
  }): Promise<IVFSResponse<void>> {
    return this.wrapResponse({
      promise: this.apiInstance.put<IVFSResponse<void>>(
        `/v2/files/actions/${fileId}/template-outputs/${templateId}`,
        {
          responseText,
          promptTitle,
        },
      ),
      errorMessage: `Failed to save template output: ${templateId}`,
    });
  }

  /**
   * Add file tag
   */
  async addFileTag({
    fileId,
    tag,
  }: {
    fileId: string;
    tag: Tag;
  }): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.post<IVFSResponse<void>>(
        `/v2/files/${fileId}/tag`,
        tag,
      ),
      errorMessage: `Failed to add tag to file: ${fileId}`,
    });
  }

  /**
   * Get thumbnail by timestamp
   */
  async getThumbnailByTimestamp({
    fileId,
    timestamp,
  }: {
    fileId: string;
    timestamp: number;
  }): Promise<IVFSResponse<{ url: string }>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<IVFSResponse<{ url: string }>>(
        `/v2/files/${fileId}/thumb/${timestamp}`,
        {
          timestamp,
        },
      ),
      errorMessage: `Failed to get thumbnail for file: ${fileId}`,
    });
  }

  /**
   * Get folders
   */
  async getFolders({
    teamId,
  }: {
    teamId: string;
  }): Promise<IVFSResponse<{ folders: FolderType[] }>> {
    return this.wrapResponse({
      promise: this.apiInstance.get<IVFSResponse<{ folders: FolderType[] }>>(
        `/v2/folders/${teamId}`,
      ),
      errorMessage: `Failed to get folders for team: ${teamId}`,
    });
  }

  /**
   * Update folder permissions
   */
  async updateFolderPermissions(
    folderId: string,
    permissions: FolderPermission[] | null,
  ): Promise<IVFSResponse<{ folder: FolderType }>> {
    if (permissions === null) {
      // DELETE request to remove all permissions (make public)
      return this.wrapResponse({
        promise: this.apiInstance.delete<IVFSResponse<{ folder: FolderType }>>(
          `/v2/folders/${folderId}/permissions`,
        ),
        errorMessage: `Failed to remove permissions for folder: ${folderId}`,
      });
    }

    // PUT request to update permissions
    return this.wrapResponse({
      promise: this.apiInstance.put<IVFSResponse<{ folder: FolderType }>>(
        `/v2/folders/${folderId}/permissions`,
        permissions,
      ),
      errorMessage: `Failed to update permissions for folder: ${folderId}`,
    });
  }

  /**
   * Remove user permission from a file
   */
  async removeFilePermission(
    fileId: string,
    userId: string,
  ): Promise<IVFSResponse<{ permissions: FolderPermission[] }>> {
    return this.wrapResponse({
      promise: this.apiInstance.delete<
        IVFSResponse<{ permissions: FolderPermission[] }>
      >(`/v2/files/${fileId}/permissions/${userId}`),
      errorMessage: `Failed to remove permission from file`,
    });
  }

  /**
   * Remove user permission from a folder
   */
  async removeFolderPermission(
    folderId: string,
    userId: string,
  ): Promise<IVFSResponse<{ permissions: FolderPermission[] }>> {
    return this.wrapResponse({
      promise: this.apiInstance.delete<
        IVFSResponse<{ permissions: FolderPermission[] }>
      >(`/v2/folders/${folderId}/permissions/${userId}`),
      errorMessage: `Failed to remove permission from folder`,
    });
  }

  /**
   * Folder actions
   */
  async folderActions({
    folderId,
    action,
    data,
  }: {
    folderId: string;
    action: 'add' | 'rename' | 'manage' | 'delete';
    data?: {
      newName?: string;
    };
  }): Promise<IVFSResponse<{ folders: FolderType[]; folderAction: string }>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<
        IVFSResponse<{ folders: FolderType[]; folderAction: string }>
      >(`/v2/folders/${folderId}/${action}`, {
        data,
      }),
      errorMessage: `Failed to perform ${action} action on folder: ${folderId}`,
    });
  }

  /**
   * Update speaker
   */
  async updateSpeaker({
    fileId,
    speakerId,
    speaker,
  }: {
    fileId: string;
    speakerId: string;
    speaker: {
      name: string;
      picture?: string;
      userId?: string;
    };
  }): Promise<IVFSResponse<{ speakers: { [key: string]: Speaker } }>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<
        IVFSResponse<{ speakers: { [key: string]: Speaker } }>
      >(`/v2/files/actions/${fileId}/speaker/${speakerId}`, speaker),
      errorMessage: `Failed to update speaker: ${speakerId}`,
    });
  }

  /**
   * Set preferred translation language for a file
   */
  async setTranslationLanguage({
    fileId,
    language,
  }: {
    fileId: string;
    language: string | null;
  }): Promise<IVFSResponse<{ preferredTranslationLanguage: string | null }>> {
    return this.wrapResponse({
      promise: this.apiInstance.put<
        IVFSResponse<{ preferredTranslationLanguage: string | null }>
      >(`/v2/files/actions/${fileId}/translation-language`, { language }),
      errorMessage: `Failed to set translation language for file: ${fileId}`,
    });
  }

  /**
   * Search transcripts within a team
   */
  async searchTranscripts({
    teamId,
    query,
    ownerIds,
    parentIds,
    createdAfter,
    createdBefore,
  }: {
    teamId: string;
    query: string;
    ownerIds?: string[];
    parentIds?: string[];
    createdAfter?: string;
    createdBefore?: string;
  }): Promise<
    IVFSResponse<{
      entries: TranscriptSearchResult[];
      pagination: {
        page: number;
        limit: number;
        total: number;
      };
    }>
  > {
    return this.wrapResponse({
      promise: this.apiInstance.post<
        IVFSResponse<{
          entries: TranscriptSearchResult[];
          pagination: {
            page: number;
            limit: number;
            total: number;
          };
        }>
      >(`/v2/files/search/${teamId}`, {
        query,
        ownerIds,
        parentIds,
        createdAfter,
        createdBefore,
      }),
      errorMessage: 'Failed to search transcripts',
    });
  }

  /**
   * Send team invitation with optional resources
   */
  async sendInvitation(
    teamId: string,
    params: SendInvitationParams,
  ): Promise<IVFSResponse<SendInvitationResponse>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<IVFSResponse<SendInvitationResponse>>(
        `/v2/team/${teamId}/invitations/send`,
        params,
      ),
      errorMessage: 'Failed to send invitation',
    });
  }

  /**
   * Accept team invitation
   */
  async acceptInvitation(
    inviteToken: string,
  ): Promise<IVFSResponse<AcceptInvitationResponse>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<IVFSResponse<AcceptInvitationResponse>>(
        `/v2/invitations/accept`,
        { inviteToken },
      ),
      errorMessage: 'Failed to accept invitation',
    });
  }

  /**
   * Get shared with me items
   */
  async getSharedWithMe(): Promise<IVFSResponse<GetSharedWithMeResponse>> {
    return this.wrapResponse({
      promise:
        this.apiInstance.get<IVFSResponse<GetSharedWithMeResponse>>(
          `/v2/shared-with-me`,
        ),
      errorMessage: 'Failed to get shared items',
    });
  }

  /**
   * Remove shared with me bookmark
   */
  async removeSharedWithMe(itemId: string): Promise<IVFSResponse<void>> {
    return this.wrapResponse({
      promise: this.apiInstance.delete<IVFSResponse<void>>(
        `/v2/shared-with-me/${itemId}`,
      ),
      errorMessage: `Failed to remove shared item: ${itemId}`,
    });
  }

  /**
   * Share a folder publicly
   */
  async shareFolder(params: {
    folderId: string;
    permissions?: SharePermission[];
  }): Promise<IVFSResponse<{ shareId: string }>> {
    return this.wrapResponse({
      promise: this.apiInstance.post<IVFSResponse<{ shareId: string }>>(
        `/v2/folders/actions/${params.folderId}/share`,
        {
          permissions: params.permissions,
        },
      ),
      errorMessage: `Failed to share folder: ${params.folderId}`,
    });
  }

  /**
   * Remove public sharing for a folder
   */
  async unshareFolder(folderId: string): Promise<IVFSResponse<void>> {
    return this.wrapResponse({
      promise: this.apiInstance.delete<IVFSResponse<void>>(
        `/v2/folders/actions/${folderId}/share`,
      ),
      errorMessage: `Failed to unshare folder: ${folderId}`,
    });
  }

  /**
   * Get a shared folder by share ID
   */
  async getSharedFolder(shareId: string): Promise<
    IVFSResponse<{
      folder: FolderType;
      files: FileType[];
    }>
  > {
    return this.wrapResponse({
      promise: this.apiInstance.get<
        IVFSResponse<{
          folder: FolderType;
          files: FileType[];
        }>
      >(`/v2/folders/shared/${shareId}`),
      errorMessage: `Failed to get shared folder: ${shareId}`,
    });
  }

  /**
   * Get a file via folder share
   */
  async getFileViaFolderShare(
    folderShareId: string,
    fileId: string,
  ): Promise<IVFSResponse<GetFileReturnType>> {
    return this.wrapResponse({
      promise: this.apiInstance.get<IVFSResponse<GetFileReturnType>>(
        `/v2/folders/shared/${folderShareId}/files/${fileId}`,
      ),
      errorMessage: `Failed to get file via folder share: ${fileId}`,
    });
  }
}

export default FilesAPI;
